import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnInit } from '@angular/core';

import { CurrencyItem, SaleAdvanceAdjustmentBO, SaleAdvanceAdjustmentRepositoryBase, SaleAdvanceBO, SaleAdvanceRepositoryBase, SaleDocumentAdjustmentBO, SaleDocumentAdjustmentRepositoryBase, SaleDocumentBO, SaleDocumentRepositoryBase, SaleProFormaBO, SaleProFormaRepositoryBase } from '@skylab-shared/business';
import { BusinessObjectBase, ModelBase, RepositoryBase, RepositoryResponseCode, RepositorySaveResponse } from '@skylab-shared/core';
import { DocumentPaymentListItemModel, KsefDocumentStatus, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';

import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SaleAdvanceAdjustmentRepository, SaleAdvanceRepository, SaleDocumentAdjustmentRepository, SaleDocumentRepository, SaleProFormaRepository } from '@skylab-angular-shared/business';
import { L10n, L10nBase, RootContext, RootContextBase, RoutingService } from '@skylab-angular-shared/core';
import { CompanyContextLink, TranslatePipe } from '@skylab-angular-shared/pipes';
import { BooleanValueAccessorDirective, ButtonComponent, DatepickerValueAccessorDirective, DialogContentComponentBase, DialogContentComponentIns, DialogInsService, FormContainerComponent, IconComponent, InsDatepickerDirective, NumericValueAccessorDirective, ToastService, TooltipDirective, ValidateDirective, ValidateInnerDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';
import { NavigationRoute } from '@skylab-shared/scenes';
import { DocumentsRouting } from '../../documents/documents-routing';
import { ViewValidation, ViewValidationService } from '../../form/form-validation';

type WithTaxLiabilityMixinBO = SaleDocumentBO | SaleDocumentAdjustmentBO | SaleAdvanceBO | SaleAdvanceAdjustmentBO;

export type SalePaymentDialogEntity = {
    dueDate: Date;
    documentNumber: string;
    grossValue: number;
    ksefDocumentStatus: KsefDocumentStatus;
    version: string;
    leftToPay: number;
    id: number;
    currency: CurrencyItem;
    kind: SaleDocumentKind;
    finalAdvance?: boolean;
    advanceValue?: number;
    delay?: number;
}

export interface SaleDocumentPaymentDialogData {
    entity: SalePaymentDialogEntity;
    onSave: (newEntity: { version: string; leftToPay: number; }) => Promise<void>;
}

interface PaymentDialogViewModel {
    bo: SaleDocumentBO | SaleProFormaBO | SaleAdvanceBO | SaleDocumentAdjustmentBO | SaleAdvanceAdjustmentBO;
    currency: Pick<CurrencyItem, 'symbol' | 'precision'>;
    isSaving: boolean;
    payment: { value: number; date: Date; onDocument: boolean; sun: string };
    getDocumentRouteObject(): NavigationRoute;
    save: () => Promise<void>;
}

@Component({
    selector: 'skl-sale-document-payment',
    templateUrl: './sale-document-payment.dialog.component.html',
    imports: [RouterLink, CompanyContextLink, DialogContentComponentIns, TooltipDirective, FormContainerComponent, ValidationSourceDirective, InsDatepickerDirective, DatepickerValueAccessorDirective, FormsModule, ValidateDirective, NumericValueAccessorDirective, ValidateInnerDirective, BooleanValueAccessorDirective, ButtonComponent, TranslatePipe, IconComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SaleDocumentPaymentDialogComponent extends DialogContentComponentBase<PaymentDialogViewModel, SaleDocumentPaymentDialogData> implements OnInit {
    public isFormReady = false;
    public viewValidation!: ViewValidation;

    public constructor(
        @Inject(L10n) private readonly l10n: L10nBase,
        @Inject(SaleDocumentRepository) private readonly saleDocumentRepository: SaleDocumentRepositoryBase,
        @Inject(SaleProFormaRepository) private readonly proFormaRepository: SaleProFormaRepositoryBase,
        @Inject(SaleAdvanceRepository) private readonly advanceRepository: SaleAdvanceRepositoryBase,
        @Inject(SaleAdvanceAdjustmentRepository) private readonly advanceAdjustmentRepository: SaleAdvanceAdjustmentRepositoryBase,
        @Inject(SaleDocumentAdjustmentRepository) private readonly saleDocumentAdjustmentRepository: SaleDocumentAdjustmentRepositoryBase,
        @Inject(RootContext) private readonly rootContext: RootContextBase,
        private readonly viewValidationService: ViewValidationService,
        protected override readonly dialogService: DialogInsService,
        private readonly routingService: RoutingService,
        private readonly toastService: ToastService
    ) {
        super(dialogService);
    }

    public override async ngOnInit(): Promise<void> {
        await this.prepareDataAsync();
        this.isFormReady = true;
    }

    protected override async prepareDataAsync(): Promise<void> {
        try {
            const entity = this.data.entity;
            const repo = this.getRepository();
            const bo = await repo.loadAsync(entity.id, this.rootContext.createLocalContext('documentPaymentHintbox'));
            await bo.waitForProcessingCompletionAsync();
            bo.attachReadonlyChecker();

            //#region advance stuff
            /*
                for non-final-advance there can only be one payment
                but hitnbox behave like it adds new payment
                in secret it doesnt add new payment but updates existing one:
                newPaymentValue = exisitingPaymentValue + controlPaymentValue
            */
            const isNonFinalAdvance = entity.kind === SaleDocumentKind.AdvanceInvoice && !entity.finalAdvance;
            const isEditAdvancePayment = isNonFinalAdvance && bo.model.payments.length;
            const originalPayment = isEditAdvancePayment ? { value: bo.model.payments[0].value, date: bo.model.payments[0].date } : undefined;
            //#endregion

            const payment = new DocumentPaymentListItemModel();
            const currency = entity.currency;

            this.viewValidation = this.viewValidationService.create([bo]);

            if (isEditAdvancePayment && originalPayment) {
                bo.model.payments[0].value = Calc.round(originalPayment.value + Math.abs(entity.leftToPay), currency.precision, MidpointRounding.AwayFromZero);
            } else {
                bo.model.payments.push(payment);
                payment.value = Math.abs(entity.leftToPay);
                payment.date = new Date();
                payment.onDocument = false;
            }
            if (bo.model.issuedByVatPayer && this.isTaxLiabilityDateChangeAvailable(bo)) bo.recalculateTaxLiabilityDate();
            const self = this;
            this.viewModel = {
                bo,
                currency: currency,
                payment: {
                    get value() {
                        return payment.value;
                    },
                    set value(value) {
                        payment.value = Math.abs(value);
                        if (isEditAdvancePayment && originalPayment)
                            bo.model.payments[0].value = Calc.round(originalPayment.value + value, currency.precision, MidpointRounding.AwayFromZero);

                    },
                    get date() {
                        return payment.date;
                    },
                    set date(date) {
                        payment.date = date;
                        if (isEditAdvancePayment && originalPayment)
                            bo.model.payments[0].date = payment.date;
                        if (bo.model.issuedByVatPayer && self.isTaxLiabilityDateChangeAvailable(bo))
                            bo.recalculateTaxLiabilityDate();
                    },
                    get onDocument() {
                        return payment.onDocument;
                    },
                    set onDocument(onDocument: boolean) {
                        payment.onDocument = onDocument;
                    },
                    get sun() {
                        return payment.sun;
                    },

                },
                isSaving: false,
                save: async () => {
                    await bo.waitForProcessingCompletionAsync();
                    if (!bo.isValid) return;

                    this.viewModel.isSaving = true;
                    try {
                        const result = await this.saveAsync(bo);
                        if (result.code === RepositoryResponseCode.Success && result.object) {
                            const newLeftToPay = isNonFinalAdvance
                                ? this.getAdvanceLeftToPayValue(bo.model.payments[0].value)
                                : this.getLeftToPayValue(this.viewModel.payment.value);
                            this.data.onSave({
                                version: result.object.version,
                                leftToPay: Calc.round(newLeftToPay, currency.precision, MidpointRounding.AwayFromZero)
                            });
                        } else if (result.code === RepositoryResponseCode.ExternalError) {
                            this.toastService.showAsync({
                                type: 'error',
                                text: this.l10n.instant('scenes.shared.form.form-state-context.external-error.message'),
                            });
                        }
                        this.onCancel();
                    } catch (err) {
                        this.logger.error(err);
                    } finally {
                        this.viewModel.isSaving = false;
                    }
                },
                getDocumentRouteObject() {
                    return {
                        url: DocumentsRouting.getEditState(self.data.entity.kind).url,
                        params: { id: self.data.entity.id }
                    }
                }
            };
        } catch (err) {
            this.logger.error(err);
            this.onCancel();
        }
    }

    public override ngOnDestroy(): void {
        this.viewValidation?.dispose();
        this.viewModel?.bo.dispose();
    }

    private isTaxLiabilityDateChangeAvailable(bo: WithTaxLiabilityMixinBO | SaleProFormaBO): bo is WithTaxLiabilityMixinBO {
        return (<WithTaxLiabilityMixinBO>bo).recalculateTaxLiabilityDate !== undefined;
    }

    private getLeftToPayValue(valueOfLastPayment: number): number {
        return this.data.entity.leftToPay > 0 ? this.data.entity.leftToPay - valueOfLastPayment : this.data.entity.leftToPay + valueOfLastPayment;
    }

    private getAdvanceLeftToPayValue(valueOfAllPayments: number) {
        const entity = this.data.entity;
        if (entity.delay != null) return 0;
        const advanceValue = entity.advanceValue ?? 0;
        return entity.leftToPay > 0 ? advanceValue - valueOfAllPayments : advanceValue + valueOfAllPayments;
    }

    private saveAsync(bo: BusinessObjectBase<ModelBase>): Promise<RepositorySaveResponse> {
        const repo = this.getRepository() as RepositoryBase<BusinessObjectBase<ModelBase>, ModelBase>;
        return repo.saveAsync(bo);
    }

    private getRepository(): SaleDocumentRepositoryBase | SaleProFormaRepositoryBase | SaleAdvanceRepositoryBase | SaleAdvanceAdjustmentRepositoryBase | SaleDocumentAdjustmentRepositoryBase {
        switch (this.data.entity.kind) {
            case SaleDocumentKind.Invoice:
            case SaleDocumentKind.Receipt:
            case SaleDocumentKind.VatMarginInvoice:
                return this.saleDocumentRepository;
            case SaleDocumentKind.InvoiceAdjustment:
            case SaleDocumentKind.ReceiptAdjustment:
            case SaleDocumentKind.VatMarginInvoiceAdjustment:
                return this.saleDocumentAdjustmentRepository;

            case SaleDocumentKind.AdvanceInvoice:
                return this.advanceRepository;
            case SaleDocumentKind.AdvanceInvoiceAdjustment:
                return this.advanceAdjustmentRepository;

            case SaleDocumentKind.ProForma:
                return this.proFormaRepository;
        }
    }
}